import { Tag } from "lucide-react";
import { Column } from "../../components/DataTable";
import { StatusBadge } from "../../components/StatusBadge";
import { Tip } from "../../components/ui/tooltip";
import { DOC_TYPE_COLORS } from "./constants";
import { formatDocType, mapLifecycleToVariant } from "./utils";
import type { GovDocRow, EvidenceRow } from "./types";

/* ── Table columns for governance documents ── */
export const getGovDocColumns = (
  page: number,
  pageSize: number,
): Column<GovDocRow>[] => [
  {
    key: "sno",
    header: "#",
    align: "center",
    render: (_row, idx) => (
      <span className="text-xs font-medium text-muted-foreground">
        {(page - 1) * pageSize + idx + 1}
      </span>
    ),
  },
  {
    key: "title",
    header: "Title",
    sortable: true,
    align: "left",
    headerAlign: "center",
    render: (row) => (
      <span className="block font-semibold break-words whitespace-normal">
        {row.title}
      </span>
    ),
  },
  {
    key: "documentType",
    header: "Type",
    sortable: true,
    align: "center",
    render: (row) => (
      <span
        className={`inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-bold min-w-[5.5rem] ${DOC_TYPE_COLORS[row.documentType] ?? "bg-[#f1f5f9] dark:bg-white/[0.05] text-[#475569] dark:text-white/60"}`}
      >
        {formatDocType(row.documentType)}
      </span>
    ),
  },
  {
    key: "currentVersion",
    header: "Version",
    sortable: true,
    align: "center",
    render: (row) => (
      <span className="font-mono text-xs">v{row.currentVersion}</span>
    ),
  },
  {
    key: "status",
    header: "Status",
    align: "center",
    render: (row) => (
      <StatusBadge
        status={
          mapLifecycleToVariant(row.status) as Parameters<
            typeof StatusBadge
          >[0]["status"]
        }
        dot={false}
      />
    ),
  },
  {
    key: "createdAt",
    header: "Created On",
    sortable: true,
    align: "center",
    render: (row) => <>{row.createdAt}</>,
  },
  {
    key: "updatedAt",
    header: "Updated On",
    sortable: true,
    align: "center",
    render: (row) => <>{row.updatedAt}</>,
  },
];

/* ── Table columns for evidence ── */
export const getEvidenceColumns = (
  page: number,
  pageSize: number,
): Column<EvidenceRow>[] => [
  {
    key: "sno",
    header: "#",
    align: "center",
    render: (_row, idx) => (
      <span className="text-xs font-medium text-muted-foreground">
        {(page - 1) * pageSize + idx + 1}
      </span>
    ),
  },
  {
    key: "title",
    header: "Title",
    sortable: true,
    align: "left",
    headerAlign: "center",
    // Let the title use the full column width and wrap onto multiple lines
    // instead of truncating, so long titles stay fully readable.
    render: (row) => (
      <span className="block font-semibold break-words whitespace-normal">
        {row.title as string}
      </span>
    ),
  },
  {
    key: "tags",
    header: "Tags",
    align: "left",
    headerAlign: "center",
    render: (row) => {
      const tags = row.tags as string[];
      if (!tags.length) return <span className="text-xs text-muted-foreground">-</span>;
      return (
        <div className="flex flex-wrap gap-1 py-1 max-w-[300px]">
          {tags.map((tag) => (
            <Tip
              key={tag}
              label={tag}
              side="top"
              className="inline-flex max-w-[90px]"
            >
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-50 dark:bg-indigo-500/10 text-[10px] font-medium text-indigo-600 dark:text-indigo-400 w-full truncate">
                <Tag className="size-2.5 flex-shrink-0" />
                <span className="truncate">{tag}</span>
              </span>
            </Tip>
          ))}
        </div>
      );
    },
  },
  {
    key: "createdAt",
    header: "Created On",
    sortable: true,
    align: "center",
    render: (row) => <>{row.createdAt}</>,
  },
  {
    key: "updatedAt",
    header: "Updated On",
    sortable: true,
    align: "center",
    render: (row) => <>{row.updatedAt}</>,
  },
];
